import UAParser from "ua-parser-js";
import * as amplitude from "@amplitude/analytics-browser";
import { windowIsExist } from "@/utils/browser";
import { getCookieValue, setCookieValue } from "@/utils/cookies";
import { Environment } from "@/utils/environment";
import { hashUserProfileEmail } from "@/utils/userProfileHash";
import {
  AMPLITUDE_INIT_OPTIONS,
  getAmplitudeApiHeaderNames,
  getAmplitudeMandatoryEventProperties,
  getAmplitudeProject,
  resolveAmplitudeProject,
} from "./config";

export {
  AMPLITUDE_CANONICAL_PROJECTS,
  AMPLITUDE_EXCLUDE_REFERRERS,
  AMPLITUDE_INIT_OPTIONS,
  AMPLITUDE_PROJECT,
  AMPLITUDE_PROJECT_EASEGOV,
  AMPLITUDE_PROJECT_MYPASSPORTHUB,
  AMPLITUDE_PROJECT_MYUSAPASSPORT,
  AMPLITUDE_PROJECT_MYUSAPASSPORTS,
  getAmplitudeApiHeaderNames,
  getAmplitudeMandatoryEventProperties,
  getAmplitudeProject,
  isSharedPaymentHost,
  normalizeAmplitudeProject,
  resolveAmplitudeProject,
} from "./config";
export type { AmplitudeCanonicalProject, AmplitudeMandatoryEventProperties } from "./config";
export { hashUserProfileEmail } from "@/utils/userProfileHash";

const AMPLITUDE_USER_ID_COOKIE = "amplitude_user_id";

/** Validity window for cross-domain amp_session_id / amp_device_id URL params. */
export const AMPLITUDE_CROSS_DOMAIN_TTL_MS = 10 * 60 * 1000;

let isInitialized = false;

export const getStoredAmplitudeUserId = (): string | undefined => {
  const value = getCookieValue(AMPLITUDE_USER_ID_COOKIE);
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

const persistAmplitudeUserId = (userId: string) => {
  setCookieValue(AMPLITUDE_USER_ID_COOKIE, userId);
};

const applyAmplitudeUserId = (userId: string) => {
  // Avoid redundant $identify from setUserId when the id is already active.
  if (amplitude.getUserId() === userId) {
    persistAmplitudeUserId(userId);
    return;
  }

  amplitude.setUserId(userId);
  persistAmplitudeUserId(userId);
};

type BuildInitOptionsParams = {
  userId?: string;
  deviceId?: string;
  sessionId?: number;
  isCrossDomainSessionRestore?: boolean;
};

const buildInitOptions = ({
  userId,
  deviceId,
  sessionId,
  isCrossDomainSessionRestore = false,
}: BuildInitOptionsParams) => {
  if (!userId && !deviceId && sessionId == null && !isCrossDomainSessionRestore) {
    return AMPLITUDE_INIT_OPTIONS;
  }

  const autocapture = isCrossDomainSessionRestore
    ? { ...AMPLITUDE_INIT_OPTIONS.autocapture, sessions: false }
    : AMPLITUDE_INIT_OPTIONS.autocapture;

  return {
    ...AMPLITUDE_INIT_OPTIONS,
    autocapture,
    ...(userId ? { userId } : {}),
    ...(deviceId ? { deviceId } : {}),
    ...(sessionId != null ? { sessionId } : {}),
  };
};

const isAmplitudeCrossDomainTimestampValid = (timestampParam: string | null): boolean =>
  !timestampParam || (!Number.isNaN(Number(timestampParam)) && Number(timestampParam) > Date.now());

export const parseAmplitudeCrossDomainParams = (search: string) => {
  const urlParams = new URLSearchParams(search);
  const isTimestampValid = isAmplitudeCrossDomainTimestampValid(urlParams.get("amp_timestamp"));

  const ampSessionIdParam = urlParams.get("amp_session_id");
  const ampSessionId =
    isTimestampValid && ampSessionIdParam && !Number.isNaN(Number(ampSessionIdParam))
      ? Number(ampSessionIdParam)
      : undefined;

  const ampDeviceId = isTimestampValid ? urlParams.get("amp_device_id") || undefined : undefined;

  return {
    ampDeviceId,
    ampSessionId,
    isCrossDomainSessionRestore: ampSessionId != null,
  };
};

/** Appends Amplitude cross-domain params to a URLSearchParams for cross-site redirects. */
export const appendAmplitudeCrossDomainParams = (params: URLSearchParams): void => {
  const sessionId = getAmplitudeSessionId();
  const deviceId = getAmplitudeDeviceId();

  if (sessionId != null) {
    params.set("amp_session_id", String(sessionId));
  }

  if (deviceId) {
    params.set("amp_device_id", deviceId);
  }

  params.set("amp_project", getAmplitudeProject());
  params.set("amp_timestamp", String(Date.now() + AMPLITUDE_CROSS_DOMAIN_TTL_MS));
};

export const getAmplitudeUserDeviceType = (): string => {
  const parser = new UAParser();
  const userAgent = parser.getResult();

  if (userAgent.device.type === "mobile") return "Mobile";
  if (userAgent.device.type === "tablet") return "Tablet";
  return "Desktop";
};

/** sessionStorage key: skip $identify when domain / live_mode / device / project did not change. */
const AMPLITUDE_IDENTIFY_FINGERPRINT_KEY = "amp_identify_fingerprint";

type AmplitudeIdentifyProps = {
  domain: string;
  liveMode: boolean;
  userDevice: string;
  project: string;
};

const buildAmplitudeIdentifyFingerprint = (props: AmplitudeIdentifyProps): string =>
  JSON.stringify(props);

const readAmplitudeIdentifyFingerprint = (): string | null => {
  if (!windowIsExist) return null;
  try {
    return sessionStorage.getItem(AMPLITUDE_IDENTIFY_FINGERPRINT_KEY);
  } catch {
    return null;
  }
};

const writeAmplitudeIdentifyFingerprint = (fingerprint: string): void => {
  if (!windowIsExist) return;
  try {
    sessionStorage.setItem(AMPLITUDE_IDENTIFY_FINGERPRINT_KEY, fingerprint);
  } catch {
    // Ignore quota / private-mode storage errors.
  }
};

const isIdentifyEvent = (event: amplitude.Types.Event): boolean =>
  event.event_type === "$identify" || event.event_type === "user_profile_changed";

/**
 * Enrichment plugin: injects canonical `project` into every outgoing Amplitude event
 * (custom track, autocapture session_start / page views, and Identify / user_profile_changed).
 * Fake marketing landings (/ls/*) outside this Next app are out of scope — they need their own SDK setup.
 */
export const createProjectEnrichmentPlugin = (
  activeProject: string
): amplitude.Types.EnrichmentPlugin => ({
  name: "project-enrichment",
  type: "enrichment",
  setup: async () => undefined,
  execute: async (event: amplitude.Types.Event): Promise<amplitude.Types.Event | null> => {
    event.event_properties = {
      ...event.event_properties,
      project: activeProject,
    };

    if (isIdentifyEvent(event) && "user_properties" in event) {
      event.user_properties = {
        ...event.user_properties,
        project: activeProject,
      };
    }

    return event;
  },
});

/**
 * Sends user properties via Identify only when values changed since the last send
 * on this origin (sessionStorage). Uses setOnce for static props to avoid overwrites.
 * Returns true when an $identify was actually sent.
 */
export const syncAmplitudeUserProperties = (domain = ""): boolean => {
  const props: AmplitudeIdentifyProps = {
    domain,
    liveMode: Environment.isProd(),
    userDevice: getAmplitudeUserDeviceType(),
    project: getAmplitudeProject(),
  };
  const fingerprint = buildAmplitudeIdentifyFingerprint(props);

  if (readAmplitudeIdentifyFingerprint() === fingerprint) {
    return false;
  }

  const identify = new amplitude.Identify();
  // Static for the session/device — setOnce so re-sends (e.g. domain change) do not overwrite.
  identify.setOnce("User Device", props.userDevice);
  identify.setOnce("live_mode", props.liveMode);
  identify.set("project", props.project);
  if (domain) {
    // Domain can change on cross-domain redirects.
    identify.set("domain", domain);
  }
  amplitude.identify(identify);
  writeAmplitudeIdentifyFingerprint(fingerprint);
  return true;
};

/** @deprecated Prefer syncAmplitudeUserProperties via initAmplitude. */
export const amplitudeDeviceType = () => {
  syncAmplitudeUserProperties(windowIsExist ? window.location.hostname : "");
};

export const initAmplitude = () => {
  const amplitudeKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || null;
  const domain = windowIsExist ? window.location.hostname : "";
  const storedUserId = getStoredAmplitudeUserId();

  if (!amplitudeKey || !windowIsExist || isInitialized) return;

  isInitialized = true;

  const { ampDeviceId, ampSessionId, isCrossDomainSessionRestore } =
    parseAmplitudeCrossDomainParams(window.location.search);
  const activeProject = resolveAmplitudeProject();
  const initOptions = buildInitOptions({
    userId: storedUserId,
    deviceId: ampDeviceId,
    sessionId: ampSessionId,
    isCrossDomainSessionRestore,
  });

  // Register enrichment before init so autocapture (session_start, etc.) is covered.
  amplitude.add(createProjectEnrichmentPlugin(activeProject));

  amplitude.init(amplitudeKey, initOptions).promise.then(() => {
    // userId is already passed in init options; only re-apply if SDK did not pick it up.
    if (storedUserId && amplitude.getUserId() !== storedUserId) {
      amplitude.setUserId(storedUserId);
    }

    // Skip $identify when user props are unchanged on this origin (e.g. SPA remount / reload).
    syncAmplitudeUserProperties(domain);

    console.log("[Amplitude] initialized", {
      apiKey: amplitudeKey ? `${amplitudeKey.slice(0, 4)}***` : null,
      domain,
      userId: storedUserId,
      initOptions,
      urlParams: {
        amp_device_id: ampDeviceId,
        amp_session_id: ampSessionId,
        amp_project: activeProject,
      },
      resolved: {
        deviceId: amplitude.getDeviceId(),
        sessionId: amplitude.getSessionId(),
        userId: amplitude.getUserId(),
      },
    });
  });
};

export const getAmplitudeSessionId = (): number | undefined => amplitude.getSessionId();

export const getAmplitudeDeviceId = (): string | undefined => amplitude.getDeviceId();

export const getAmplitudeUserId = (): string | undefined => amplitude.getUserId();

export const getAmplitudeApiHeaders = (): Record<string, string> => {
  const headerNames = getAmplitudeApiHeaderNames();
  const headers: Record<string, string> = {};

  const sessionId = getAmplitudeSessionId();
  const deviceId = getAmplitudeDeviceId();

  if (sessionId != null) {
    headers[headerNames.sessionId] = String(sessionId);
  }
  if (deviceId) {
    headers[headerNames.deviceId] = deviceId;
  }
  headers[headerNames.project] = getAmplitudeProject();

  return headers;
};

export const syncAmplitudeUserFromApplicationEmail = async (applicationData?: {
  emailAddress?: string | null;
}) => {
  if (applicationData?.emailAddress) {
    await amplitudeTrackUserFromEmail(applicationData.emailAddress);
  }
};

export const amplitudeTrackSession = (sessionId: number | string) => {
  if (process.env.NEXT_PUBLIC_ENV === "local") return;
  amplitude.setSessionId(+sessionId);
};

export const amplitudeTrackUser = (userId: string) => {
  if (process.env.NEXT_PUBLIC_ENV === "local") return;
  applyAmplitudeUserId(userId);
};

export const amplitudeTrackUserFromEmail = async (email: string | null | undefined) => {
  if (process.env.NEXT_PUBLIC_ENV === "local") return;

  const userId = await hashUserProfileEmail(email);

  if (userId) {
    applyAmplitudeUserId(userId);
  }
};

export const amplitudeTrackEvent = (name: string, data?: Record<string, any>) => {
  if (process.env.NEXT_PUBLIC_ENV === "local") return;
  amplitude.track(name, {
    ...getAmplitudeMandatoryEventProperties(),
    ...data,
  });
};
